import { isDbClosedError } from "./is-db-closed-error"
import type {
  HttpClientAdapter,
  OfflineResourceConfig,
  StorageAdapter,
  SyncMeta,
  SyncPullResponse,
} from "./types"

/**
 * HOT-tier eviction + floor refresh (STK-1322).
 *
 * A `hot` resource keeps only the rows inside the rolling window, bounded by
 * the server-computed `windowFloor` (`MIN(syncId)` over the window). This
 * module refreshes that floor and reconciles the local store to it:
 *
 *  - floor ROSE  (day-roll: window slid forward, old dates dropped out, OR the
 *    first reclaim for a pre-windowing store) → delete `syncId < newFloor`.
 *  - floor FELL  (window grew — a config `window.days` increase) → backfill the
 *    `[newFloor, oldFloor)` gap by re-pulling the windowed slice (idempotent
 *    upserts).
 *
 * Invariants:
 *  - Touches `windowFloor` ONLY, never `lastSyncId` — the delta cursor is
 *    untouched, so eviction never triggers a resync storm.
 *  - Never evicts a row with a pending/syncing local change (`keep` set from
 *    the change queue) — the user's optimistic write must survive.
 *  - REFERENCE / BACKGROUND resources are never passed here.
 *
 * Uses the base `StorageAdapter` primitives (getAll + delete) so it works
 * against every adapter (Dexie, memory, mock) with no interface change. The
 * `syncId` values round-trip through JSON as strings for BIGSERIAL precision,
 * so every comparison coerces via `Number()`. (A Dexie-native
 * `where('syncId').below(floor)` range delete is a later perf optimization;
 * eviction runs infrequently — boot + day-roll — so the scan is acceptable.)
 */

/** Windowed envelope: the shared `SyncPullResponse` gains `windowFloor` in STK-1321. */
type WindowedPullResponse = SyncPullResponse<{ id: string }> & {
  windowFloor?: number
}

interface EvictRow {
  id: string
  syncId: number | string
}

export interface HotEvictionDeps {
  storage: StorageAdapter
  httpClient: HttpClientAdapter
  /** Ids with an in-flight local change — never evicted. */
  getActiveEntityIds: (resource: string) => Promise<Set<string>>
  getSyncMeta: (resource: string) => Promise<SyncMeta | null>
  saveSyncMeta: (resource: string, meta: SyncMeta) => Promise<void>
}

/**
 * Fetch the current server `windowFloor` for a hot resource cheaply — a
 * `limit=1` windowed pull whose envelope carries the floor. Returns undefined
 * when the server doesn't report a floor (non-windowed response / empty window
 * handled by the caller as "nothing to reconcile").
 */
export async function fetchWindowFloor(
  httpClient: HttpClientAdapter,
  config: OfflineResourceConfig,
  windowStart: string
): Promise<number | undefined> {
  const response = await httpClient.get<WindowedPullResponse>(
    config.endpoints.full,
    { limit: "1", offset: "0", windowStart }
  )
  return response.windowFloor !== undefined
    ? Number(response.windowFloor)
    : undefined
}

/**
 * Refresh the floor for a single hot resource and reconcile the local store.
 * `windowStart` is the client-owned absolute date (`today − window.days`).
 */
export async function refreshHotResource(
  config: OfflineResourceConfig,
  windowStart: string,
  deps: HotEvictionDeps
): Promise<void> {
  const meta = await deps.getSyncMeta(config.name)
  // No sync record yet → cold-start's windowed fullFetch will set the floor;
  // nothing to reconcile here.
  if (!meta) return

  const oldFloor = meta.windowFloor
  const newFloor = await fetchWindowFloor(deps.httpClient, config, windowStart)
  if (newFloor === undefined) return

  if (oldFloor === undefined || newFloor > oldFloor) {
    // Rose (or first reclaim of a pre-windowing full table): drop everything
    // below the new floor, except rows with an in-flight local change.
    await evictBelow(config, newFloor, deps)
  } else if (newFloor < oldFloor) {
    // Fell (window grew): re-pull the widened slice; upserts are idempotent.
    await backfillWindow(config, windowStart, deps)
  }

  // Persist the new floor, touching `windowFloor` ONLY. Re-read the meta first:
  // the async floor fetch + reconcile above can race a live SSE delta that
  // advanced `lastSyncId` (via `updateSyncMeta`), and re-saving the pre-pass
  // snapshot would regress the delta cursor — the next reload/reconnect would
  // then replay already-applied deltas. Merge onto the freshest meta so the
  // HIGH cursor stays wherever the live path left it. If the record vanished
  // (logout/reset wiped `_syncMeta` mid-pass) there is nothing to update.
  const latestMeta = await deps.getSyncMeta(config.name)
  if (!latestMeta) return
  await deps.saveSyncMeta(config.name, { ...latestMeta, windowFloor: newFloor })
}

/** Delete rows with `syncId < floor`, skipping change-queue-active ids. */
async function evictBelow(
  config: OfflineResourceConfig,
  floor: number,
  deps: HotEvictionDeps
): Promise<void> {
  const keep = await deps.getActiveEntityIds(config.name)
  const rows = await deps.storage.getAll<EvictRow>(config.tableName)
  for (const row of rows) {
    if (Number(row.syncId) < floor && !keep.has(row.id)) {
      await deps.storage.delete(config.tableName, row.id)
    }
  }
}

/**
 * Re-pull the windowed slice (`syncId >= newFloor`) and upsert it locally.
 * Mirrors the engine's cold-start `fullFetch`: the `full` endpoint is a
 * current-state snapshot (upserts only — soft-deletes arrive as `deletedAt`
 * upserts, hard-deletes are simply absent), so there are no delete ops to
 * apply. Skips change-queue-active ids so a floor-fell refresh can never
 * overwrite the user's in-flight optimistic edit with the server copy — the
 * same guarantee `evictBelow` gives.
 */
async function backfillWindow(
  config: OfflineResourceConfig,
  windowStart: string,
  deps: HotEvictionDeps
): Promise<void> {
  const keep = await deps.getActiveEntityIds(config.name)
  const pageSize = config.pageSize ?? 1000
  let offset = 0
  while (true) {
    const response = await deps.httpClient.get<WindowedPullResponse>(
      config.endpoints.full,
      { limit: pageSize.toString(), offset: offset.toString(), windowStart }
    )
    const items = response.operations
      .filter((op) => op.op === "upsert" && !keep.has(op.id))
      .map((op) => op.data)
    if (items.length > 0) {
      await deps.storage.putMany(config.tableName, items)
    }
    // Page-fullness keys off the RAW operation count, not the filtered
    // `items` — skipping active ids (or any future op filtering) must not be
    // mistaken for a short final page, or later pages of the widened slice
    // would go unbackfilled.
    if (!response.hasMore || response.operations.length < pageSize) break
    offset += pageSize
  }
}

/**
 * Refresh every hot resource. `windowStartFor` maps a config → its absolute
 * `windowStart` (the engine owns the clock so the DSL can fake it). Non-hot
 * resources return undefined and are skipped. Per-resource failures are
 * isolated so one bad refresh never blocks the others.
 */
export async function runHotEviction(
  configs: Iterable<OfflineResourceConfig>,
  windowStartFor: (config: OfflineResourceConfig) => string | undefined,
  deps: HotEvictionDeps
): Promise<void> {
  for (const config of configs) {
    const windowStart = windowStartFor(config)
    if (windowStart === undefined) continue
    try {
      await refreshHotResource(config, windowStart, deps)
    } catch (err) {
      // The store closed under us (cross-tab logout/delete). Every remaining
      // resource would fail the same way, and the `onClose` watcher reopens +
      // restarts the engine — so stop quietly rather than logging a scary error
      // per resource.
      if (isDbClosedError(err)) return
      console.error(
        `[SyncEngine] HOT eviction failed for "${config.name}":`,
        err
      )
    }
  }
}
